import { AbstractRenderer } from './abstract-renderer';
import type { DrawInstruction } from './draw-instruction';
import type { RenderNodeFn } from './render-context';
import { RenderContext } from './render-context';
import type {
  BaseRenderNodeOptions,
  RenderNodeState,
  RenderOptions,
  StaticRenderingData,
} from './rendering-data';
import { getStaticRenderingDataAndInitialRenderNodeState } from './rendering-data';
import type { NodeBoundingBox, RenderModelDescendant, RenderModelPage } from '../../slate-api';
import { renderPage } from '../nodes/page';

/**
 * Render a page in a single pass (without caching).
 */
export class StaticRenderer extends AbstractRenderer {
  render(
    pageBoundingBox: NodeBoundingBox<RenderModelPage>,
    options: Omit<RenderOptions, 'rMPath'>
  ): DrawInstruction[] {
    const {
      rMNodeEntry: [, rMPath],
    } = pageBoundingBox;
    const { overrideRenderPage } = options;

    const { initialRenderNodeState, staticRenderingData } =
      getStaticRenderingDataAndInitialRenderNodeState({ ...options, rMPath });

    return this.renderNode({
      boundingBox: pageBoundingBox,
      renderFn: overrideRenderPage ?? renderPage,
      renderNodeOptions: { selectionHighlightAbove: false },
      renderNodeState: initialRenderNodeState,
      staticRenderingData,
    });
  }

  private renderNode<N extends RenderModelDescendant, O extends BaseRenderNodeOptions>(options: {
    boundingBox: NodeBoundingBox<N>;
    renderFn: RenderNodeFn<N, O>;
    renderNodeOptions: O;
    renderNodeState: RenderNodeState;
    staticRenderingData: StaticRenderingData;
  }): DrawInstruction[] {
    const { boundingBox, renderFn, renderNodeOptions, renderNodeState, staticRenderingData } =
      options;

    const renderContext = new RenderContext(this.editor, { renderNodeState, staticRenderingData });
    const { renderInstructions } = renderContext.renderNode(
      boundingBox,
      renderFn,
      renderNodeOptions
    );

    return renderInstructions.flatMap(renderInstruction => {
      if (renderInstruction.type !== 'renderNode') return renderInstruction;

      const {
        boundingBox: childBoundingBox,
        options: renderChildOptions,
        renderFn: renderChildFn,
        renderNodeState: renderChildState,
      } = renderInstruction;

      return this.renderNode({
        boundingBox: childBoundingBox,
        renderFn: renderChildFn,
        renderNodeOptions: renderChildOptions,
        renderNodeState: renderChildState,
        staticRenderingData,
      });
    });
  }
}
